import {createFeatureSelector, createSelector} from '@ngrx/store';
import {IUserAccessState} from './user-access.reducer';
import {selectSharedState} from '../../core/store-shared/shared.selectors';
import {SharedState} from '../../core/store-shared/shared.reducer';
import {ApplicationFeatureKeys, ApplicationFeaturesEnum} from '../../../shared/shared-utils/features/features';
import {IUserAccessToken, IUserAccessTokenURL} from '../models';
import {ISettingsState, IUserAccessFeatures} from '../models/user-access-features/user-access-features';
import {NavigationPageEnum} from '../models/navigations.enums';
import {
  _DEFAULT_USER_ACCESS_FEATURE_LOCAL_STORAGE_SETTINGS,
  _DEFAULT_USER_ACCESS_FEATURE_SHELF_TALKER_SETTINGS,
  _DEFAULT_USER_ACCESS_FEATURE_STOCK_TAKE_SETTINGS,
  _DEFAULT_USER_ACCESS_STOCK_RULES,
  _DEFAULT_USER_ACCESS_USER_PROFILE,
  _DEFAULT_USER_ACCESS_USER_TABLE_SETTINGS,
  IUserAccessFeatureLocalStorageSettings,
  IUserAccessFeatureShelfTalkerSettings,
  IUserAccessFeatureStockRules,
  IUserAccessFeatureStockTakeSettings,
  IUserAccessUserProfile,
  IUserAccessUserTableSettings,
} from '../models/user-access-features/access';
import {
  _DEFAULT_USER_ACCESS_APPLICATION_PREFERENCE_SETTINGS,
  IUserAccessApplicationPreferenceSettings,
} from '../models/user-access-features/access';
import {
  _DEFAULT_USER_ACCESS_FEATURE_AUTO_ORDERING_SETTINGS,
  IUserAccessFeatureAutoOrderingSettings,
} from '../models/user-access-features/access';
import {selectSelectedStore} from '../user-stores-store/user-store.selectors';

export const selectUserAccessState = createFeatureSelector<IUserAccessState>('user-access');

//#region GET USER ACCESS

/**
 * Selector to determine if the user access data is currently loading.
 *
 * Useful for displaying loaders or disabling UI elements until access data is available.
 *
 * @returns {boolean} `true` if user access data is loading, otherwise `false`.
 */
export const selectIsUserAccessLoading = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.isUserAccessLoading,
);

/**
 * Selector to retrieve the complete user access object from the state.
 *
 * This contains access codes, store lists, and permissions for the user.
 *
 * @returns {IUserAccess | undefined} The user access object if available.
 */
export const selectUserAccess = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.userAccess,
);

/**
 * Selector to retrieve the list of store IDs associated with the user's access.
 *
 * Returns an empty array if no access data is available.
 *
 * @returns {string[]} An array of store IDs.
 */
export const selectUserAccessStoreList = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => {
    const storesList: string[] = state.userAccess?.storeList;
    return storesList?.length > 0 ? storesList : <string[]>[];
  },
);

export const selectUserAccessStoreAccess = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => {
    const storesList: Record<string, number> = state.userAccess?.stores || <Record<string, number>>{};
    return Object.keys(storesList).length > 0 ? storesList : <Record<string, number>>{};
  },
);

export const selectUserAccessAdminForStores = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => {
    const storesList: string[] = state.userAccess?.adminAccessStores;
    return storesList?.length > 0 ? storesList : <string[]>[];
  },
);

//#endregion

export const selectGroupAccess = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.groupAccess,
);

export const selectGroupAccessLoaded = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.groupAccessLoaded,
);

export const selectIsGroupAccessLoading = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.isGroupAccessLoading,
);

export const selectIsStaffAccessInitialDataLoading = createSelector(
  selectUserAccessState,
  selectSharedState,
  (userAccess: IUserAccessState, sharedCore: SharedState) =>
    !userAccess.groupAccessLoaded || sharedCore.isColleaguesLoading,
);

export const selectIsUserAccessAndColleaguesLoading = createSelector(
  selectIsStaffAccessInitialDataLoading,
  selectUserAccessState,
  (isInitialDataLoading: boolean, userAccess: IUserAccessState) =>
    userAccess.isLoading || isInitialDataLoading,
);

export const selectUserAccessOperationProgress = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.operationProgress,
);

export const selectLastFailedStaffAccessCreateUserId = createSelector(
  selectUserAccessState,
  (state: IUserAccessState): string | null => state.lastFailedStaffAccessCreateUserId,
);

export const selectLastFailedColleagueAccessUpdateUserId = createSelector(
  selectUserAccessState,
  (state: IUserAccessState): string | null => state.lastFailedColleagueAccessUpdateUserId,
);

export const selectUserAccessLoadingPresentation = createSelector(
  selectUserAccessState,
  selectSharedState,
  (userAccess: IUserAccessState, sharedCore: SharedState) => {
    const isInitialDataLoading =
      !userAccess.groupAccessLoaded ||
      sharedCore.isColleaguesLoading;
    const isBusy = userAccess.isLoading || isInitialDataLoading;
    if (!isBusy) {
      return null;
    }

    if (!userAccess.isLoading && isInitialDataLoading) {
      return {
        step: 1,
        totalSteps: 1,
        message: 'Loading staff access…',
        stepLabels: ['Load staff access'],
      };
    }

    const base = userAccess.operationProgress;
    if (!base) {
      return {
        step: 1,
        totalSteps: 1,
        message: sharedCore.isColleaguesLoading ? 'Refreshing user list…' : 'Updating staff access…',
        stepLabels: [sharedCore.isColleaguesLoading ? 'Refresh user list' : 'Working…'],
      };
    }

    if (sharedCore.isColleaguesLoading && !userAccess.isLoading && base.totalSteps > 0) {
      return {
        step: base.totalSteps,
        totalSteps: base.totalSteps,
        message: 'Refreshing user list…',
        stepLabels: [...base.stepLabels.slice(0, -1), 'Refresh user list'],
      };
    }

    return base;
  },
);

export const selectIsUserAccessTokenLoading = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.isUserAccessTokenLoading,
);

export const selectUserAccessTokenSelectedFeature = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.userAccessTokenFeature,
);

export const selectIsDashboardReportingTokenRequested = createSelector(
  selectUserAccessTokenSelectedFeature,
  (feature: ApplicationFeatureKeys) => feature === ApplicationFeaturesEnum.DASHBOARD_REPORT,
);

export const selectUserAccessToken = createSelector(
  selectUserAccessState,
  (state: IUserAccessState) => state.userAccessToken as IUserAccessToken,
);

export const selectUserAccessTokenURL = createSelector(
  selectUserAccessState,
  selectUserAccessTokenSelectedFeature,
  (state: IUserAccessState, feature: ApplicationFeatureKeys) => {
    if (!feature) {
      return undefined;
    }
    if (feature === ApplicationFeaturesEnum.DASHBOARD_REPORT) {
      return state.userAccessToken as IUserAccessTokenURL<{ userToken: string; }>;
    }
  },
);

//#region SELECT - USER ACCESS FEATURE SETTINGS
export const selectUserAccessFeatureSettings = createSelector(
  selectUserAccessState,
  (state: IUserAccessState): ISettingsState => {
    return state.userAccessFeatureSettings;
  },
);

//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - ACCOUNT
export const selectUserAccessFeatureAccountSettings = createSelector(
  selectUserAccessFeatureSettings,
  (state: ISettingsState): IUserAccessFeatures => {
    return state.account;
  },
);

export const selectUserAccessUserProfile = createSelector(
  selectUserAccessFeatureAccountSettings,
  (feature: IUserAccessFeatures): IUserAccessUserProfile => {
    return feature[NavigationPageEnum.UserProfile]?.crudAccess ?? _DEFAULT_USER_ACCESS_USER_PROFILE;
  },
);

//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - APPLICATION
export const selectUserAccessFeatureApplicationSettings = createSelector(
  selectUserAccessFeatureSettings,
  (state: ISettingsState): IUserAccessFeatures => {
    return state.application;
  },
);

export const selectUserAccessUserTableSettings = createSelector(
  selectUserAccessFeatureAccountSettings,
  (feature: IUserAccessFeatures): IUserAccessUserTableSettings => {
    return feature[NavigationPageEnum.TableSettings]?.crudAccess ?? _DEFAULT_USER_ACCESS_USER_TABLE_SETTINGS;
  },
);

export const selectUserAccessApplicationPreferenceSettings = createSelector(
  selectUserAccessFeatureAccountSettings,
  (feature: IUserAccessFeatures): IUserAccessApplicationPreferenceSettings => {
    return feature[NavigationPageEnum.Preferences]?.crudAccess ?? _DEFAULT_USER_ACCESS_APPLICATION_PREFERENCE_SETTINGS;
  },
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURES
export const selectUserAccessFeatureFeaturesSettings = createSelector(
  selectUserAccessFeatureSettings,
  (state: ISettingsState): IUserAccessFeatures => {
    return state.features;
  },
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURE AUTO ORDERING
export const selectUserAccessFeatureAutoOrderingSettings = createSelector(
  selectUserAccessFeatureFeaturesSettings,
  (features: IUserAccessFeatures): { [storeId: string]: IUserAccessFeatureAutoOrderingSettings } => {
    return features[NavigationPageEnum.AutoOrdering]?.crudAccess || <{
      [storeId: string]: IUserAccessFeatureAutoOrderingSettings
    }>{};
  },
);

export const selectUserAccessFeatureAutoOrderingSettingsForSelectedStore = createSelector(
  selectUserAccessFeatureAutoOrderingSettings,
  selectSelectedStore,
  ((userAccess, selectedStore): IUserAccessFeatureAutoOrderingSettings => {
    return userAccess[selectedStore.storeId] ?? <IUserAccessFeatureAutoOrderingSettings>{..._DEFAULT_USER_ACCESS_FEATURE_AUTO_ORDERING_SETTINGS};
  }),
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURE SHELF TALKERS
export const selectUserAccessFeatureShelfTalkersSettings = createSelector(
  selectUserAccessFeatureFeaturesSettings,
  (features: IUserAccessFeatures): { [storeId: string]: IUserAccessFeatureShelfTalkerSettings } => {
    return features[NavigationPageEnum.ShelfTalkers]?.crudAccess || <{
      [storeId: string]: IUserAccessFeatureShelfTalkerSettings
    }>{};
  },
);

export const selectUserAccessFeatureShelfTalkerSettingsForSelectedStore = createSelector(
  selectUserAccessFeatureShelfTalkersSettings,
  selectSelectedStore,
  ((userAccess, selectedStore): IUserAccessFeatureShelfTalkerSettings => {
    return userAccess[selectedStore.storeId] ?? <IUserAccessFeatureShelfTalkerSettings>{..._DEFAULT_USER_ACCESS_FEATURE_SHELF_TALKER_SETTINGS};
  }),
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURE STOCK TAKE
export const selectUserAccessFeatureStockTakeSettings = createSelector(
  selectUserAccessFeatureFeaturesSettings,
  (features: IUserAccessFeatures): { [storeId: string]: IUserAccessFeatureStockTakeSettings } => {
    return features[NavigationPageEnum.StockTake]?.crudAccess || <{
      [storeId: string]: IUserAccessFeatureStockTakeSettings
    }>{};
  },
);

export const selectUserAccessFeatureStockTakeSettingsForSelectedStore = createSelector(
  selectUserAccessFeatureStockTakeSettings,
  selectSelectedStore,
  ((userAccess, selectedStore): IUserAccessFeatureStockTakeSettings => {
    return userAccess[selectedStore.storeId] ?? <IUserAccessFeatureStockTakeSettings>{..._DEFAULT_USER_ACCESS_FEATURE_STOCK_TAKE_SETTINGS};
  }),
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURE STOCK RULES
export const selectUserAccessFeatureStockRules = createSelector(
  selectUserAccessFeatureFeaturesSettings,
  (features: IUserAccessFeatures): { [storeId: string]: IUserAccessFeatureStockRules } => {
    return features[NavigationPageEnum.StockRules]?.crudAccess || <{
      [storeId: string]: IUserAccessFeatureStockRules
    }>{};
  },
);

export const selectUserAccessFeatureStockRulesForSelectedStore = createSelector(
  selectUserAccessFeatureStockRules,
  selectSelectedStore,
  ((userAccess, selectedStore): IUserAccessFeatureStockRules => {
    return userAccess[selectedStore.storeId] ?? <IUserAccessFeatureStockRules>{..._DEFAULT_USER_ACCESS_STOCK_RULES};
  }),
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - FEATURE LOCAL STORAGE
export const selectUserAccessFeatureLocalStorage = createSelector(
  selectUserAccessFeatureFeaturesSettings,
  (features: IUserAccessFeatures): { [storeId: string]: IUserAccessFeatureLocalStorageSettings } => {
    return features[NavigationPageEnum.LocalStorage]?.crudAccess || <{
      [storeId: string]: IUserAccessFeatureLocalStorageSettings
    }>{};
  },
);

export const selectUserAccessFeatureLocalStorageForSelectedStore = createSelector(
  selectUserAccessFeatureLocalStorage,
  selectSelectedStore,
  ((userAccess, selectedStore): IUserAccessFeatureLocalStorageSettings => {
    return userAccess[selectedStore.storeId] ?? <IUserAccessFeatureLocalStorageSettings>{..._DEFAULT_USER_ACCESS_FEATURE_LOCAL_STORAGE_SETTINGS};
  }),
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - NOTIFICATIONS
export const selectUserAccessFeatureNotificationSettings = createSelector(
  selectUserAccessFeatureSettings,
  (state: ISettingsState): IUserAccessFeatures => {
    return state.notifications;
  },
);
//#endregion

//#region SELECT - USER ACCESS FEATURE SECTION - SECURITY
export const selectUserAccessFeatureSecuritySettings = createSelector(
  selectUserAccessFeatureSettings,
  (state: ISettingsState): IUserAccessFeatures => {
    return state.security;
  },
);
//#endregion
